import type { ChatMessageContent, ToolResult, MediaPart } from '../types';
import { apiClient } from './apiClient';

/**
 * Gemini Service - Frontend API Layer
 * All AI operations are proxied through the backend API for security
 * This prevents API key exposure and enables proper rate limiting
 */

/**
 * Chat Response Generation
 * Sends chat contents to backend which handles Gemini API calls
 */
export const generateChatResponse = async (contents: { parts: any[] }): Promise<ChatMessageContent> => {
  try {
    const response = await apiClient.post<{ content: ChatMessageContent }>('/chat', { contents });
    return response.content;
  } catch (error) {
    console.error('Error generating chat response:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to generate response. Please try again.');
  }
};

/**
 * Image Generation
 * Generates images using Imagen model via backend API
 */
export const generateImage = async (prompt: string): Promise<string> => {
  try {
    const response = await apiClient.post<{ imageUrl: string }>('/generate-image', { prompt });
    return response.imageUrl;
  } catch (error) {
    console.error('Error generating image:', error);
    throw new Error('Failed to generate image. Please try again.');
  }
};

/**
 * Video Generation
 * Generates videos using Veo model via backend API with progress updates
 */
export const generateVideo = async (
  prompt: string,
  aspectRatio: '16:9' | '9:16',
  onProgress: (message: string) => void
): Promise<string> => {
  try {
    onProgress('Initializing video generation...');
    
    const response = await apiClient.post<{ operationId: string }>('/generate-video', {
      prompt,
      aspectRatio,
    });

    const operationId = response.operationId;
    onProgress('Video generation started. This may take a few minutes...');

    // Poll for video completion
    let attempts = 0;
    const maxAttempts = 60; // 10 minutes max (10s intervals)
    
    while (attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 10000)); // Wait 10 seconds
      
      try {
        const statusResponse = await apiClient.get<{ 
          done: boolean; 
          videoUrl?: string;
          error?: string;
        }>(`/video-status/${operationId}`);
        
        if (statusResponse.done) {
          if (statusResponse.videoUrl) {
            onProgress('Video ready! Loading...');
            return statusResponse.videoUrl;
          } else if (statusResponse.error) {
            throw new Error(statusResponse.error);
          }
        }
        
        onProgress(`Checking status... (attempt ${attempts + 1}/${maxAttempts})`);
        attempts++;
      } catch (error) {
        console.error('Error checking video status:', error);
        throw error;
      }
    }

    throw new Error('Video generation timed out. Please try again.');
  } catch (error) {
    console.error('Error generating video:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to generate video. Please check the console for details.');
  }
};

/**
 * Image Analysis
 * Analyzes images using Gemini vision model via backend API
 */
export const analyzeImage = async (contents: { parts: any[] }): Promise<string> => {
  try {
    const response = await apiClient.post<{ analysis: string }>('/analyze-image', { contents });
    return response.analysis;
  } catch (error) {
    console.error('Error analyzing image:', error);
    throw new Error('Failed to analyze image.');
  }
};

/**
 * Campaign Review (Opus Workflow)
 * Submits campaign for AI review via backend API
 */
export const generateCampaignReview = async (
  campaignData: { clientName: string; campaignName: string; targetAudience: string; budget: number },
  creativeAssets: MediaPart[]
): Promise<{ status: 'Approved' | 'Changes Requested'; rationale: string }> => {
  try {
    const response = await apiClient.post<{ 
      status: 'Approved' | 'Changes Requested'; 
      rationale: string;
    }>('/campaign-review', {
      campaignData,
      creativeAssets,
    });

    return response;
  } catch (error) {
    console.error('Error generating campaign review:', error);
    throw new Error('Failed to generate campaign review. Please check the console for details.');
  }
};
